import React, { useState, useMemo, useEffect } from 'react';
import { createWillWithMedia } from '../services/willService';
import MediaUploadForWill from './MediaUploadForWill';
import { Categories, getMainCategories, getSubcategories, CategoryType } from '../types/category';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search, X, Edit, Check, ArrowLeft } from "lucide-react";
import '../styles/forms.css';
import './ReviewStyles.css'; // Import new styles for review step

// Add dialog components for the examples modal
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose
} from "@/components/ui/dialog";

interface WillFormProps {
  onSuccess?: (will: any) => void;
  onCancel?: () => void;
  initialData?: {
    title?: string;
    description?: string;
    budget?: string;
    currency?: string;
    category?: string;
    location?: string;
    fulfillmentTimeType?: string;
    fulfillmentDate?: string;
    recurrencePattern?: string;
    media?: any[];
  };
}

// Local storage key for form data
const FORM_STORAGE_KEY = 'wanttd_will_form_data';

// Example templates grouped by category
const EXAMPLE_TEMPLATES = [
  {
    category: "Services",
    examples: [
      "I need a local plumber to fix a leaking pipe under my kitchen sink. The leak started yesterday and I need someone as soon as possible.",
      "Looking for a professional photographer for a family photoshoot in the park. We are a family of 5 with two young children. Prefer someone with experience photographing kids.",
      "Need a skilled tutor for high school math (calculus) to help my son prepare for his final exams. Preferably someone who can meet twice a week for the next month."
    ]
  },
  {
    category: "Items to Buy",
    examples: [
      "Looking for a second-hand iPhone 13 Pro in good condition. Prefer 256GB storage or higher, battery health at least 85%. Color doesn't matter.",
      "Want to buy a wooden dining table that seats 6-8 people. Prefer solid wood, not particleboard. My space fits something approximately 180cm x 90cm.",
      "Need a lightweight stroller for travel that can be folded and carried onto an airplane. Budget is flexible for the right option."
    ]
  },
  {
    category: "Activities & Experiences",
    examples: [
      "Looking for tickets to the sold-out concert at Central Stadium on June 15th. Need 2 tickets, preferably seated together.",
      "Want to organize a private cooking class for 6 people focusing on Italian cuisine. Would like this to happen at my home on a weekend evening.",
      "Seeking a tour guide who speaks English and Japanese for a 3-day private tour of historical sites. Planning to visit in August."
    ]
  },
  {
    category: "Professional Help",
    examples: [
      "Need a graphic designer to create a logo for my new bakery business. Looking for something modern but warm, using browns and oranges.",
      "Looking for a web developer who can build a simple e-commerce website for my handmade jewelry. Need basic product listings and secure checkout.",
      "Seeking a tax consultant familiar with freelancer taxes who can help organize my finances and prepare for yearly tax filing."
    ]
  }
];

const WillForm: React.FC<WillFormProps> = ({ onSuccess, onCancel, initialData }) => {
  // Load saved form data from localStorage on initial mount
  const loadSavedFormData = () => {
    try {
      const savedData = localStorage.getItem(FORM_STORAGE_KEY);
      if (savedData) {
        return JSON.parse(savedData);
      }
    } catch (error) {
      console.error('Error loading saved form data:', error);
    }
    return null;
  };

  // Give preference to initialData (from AI) over savedData (from localStorage)
  const savedData = loadSavedFormData();
  
  const [description, setDescription] = useState(initialData?.description || savedData?.description || '');
  const [budget, setBudget] = useState(initialData?.budget || savedData?.budget || '');
  const [location, setLocation] = useState(initialData?.location || savedData?.location || '');
  const [category, setCategory] = useState(initialData?.category || savedData?.category || '');
  const [currency, setCurrency] = useState(initialData?.currency || savedData?.currency || 'ILS');
  const [media, setMedia] = useState<any[]>(initialData?.media || savedData?.media || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(savedData?.currentStep || 1);
  const [fulfillmentTimeType, setFulfillmentTimeType] = useState(initialData?.fulfillmentTimeType || savedData?.fulfillmentTimeType || 'SPECIFIC_DATE');
  const [fulfillmentDate, setFulfillmentDate] = useState(initialData?.fulfillmentDate || savedData?.fulfillmentDate || '');
  const [recurrencePattern, setRecurrencePattern] = useState(initialData?.recurrencePattern || savedData?.recurrencePattern || 'NONE');
  const [budgetType, setBudgetType] = useState(savedData?.budgetType || 'FIXED'); // FIXED, OPEN, FREE
  const [searchQuery, setSearchQuery] = useState('');
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [examplesOpen, setExamplesOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState(''); // For the review step
  const [editingSection, setEditingSection] = useState<string | null>(null);

  // Save form data to localStorage whenever form state changes
  useEffect(() => {
    const formData = {
      description,
      budget,
      location,
      category,
      currency,
      media: media.filter(item => !item.file), // Don't store actual file objects
      currentStep,
      fulfillmentTimeType,
      fulfillmentDate,
      recurrencePattern,
      budgetType
    };
    
    localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
  }, [
    description, 
    budget, 
    location, 
    category, 
    currency, 
    media, 
    currentStep, 
    fulfillmentTimeType, 
    fulfillmentDate, 
    recurrencePattern, 
    budgetType
  ]);

  // Update the preview title whenever the description changes
  useEffect(() => {
    if (description) {
      setPreviewTitle(generateTitle(description));
    }
  }, [description]);

  // If we have initialData, we should automatically go to the review step
  useEffect(() => {
    if (initialData?.description) {
      setCurrentStep(4); // Go directly to review step
    }
  }, [initialData]);

  const mainCategories = getMainCategories();

  const filteredCategories = useMemo(() => {
    if (!searchQuery) return mainCategories;

    const query = searchQuery.toLowerCase();
    return mainCategories.filter(mainCategory => {
      const matchesMainCategory = 
        mainCategory.displayName.toLowerCase().includes(query) ||
        mainCategory.description.toLowerCase().includes(query);
      
      if (matchesMainCategory) return true;

      const subCategories = getSubcategories(mainCategory.value);
      const hasMatchingSubCategory = subCategories.some(subCategory => 
        subCategory.displayName.toLowerCase().includes(query) ||
        subCategory.description.toLowerCase().includes(query)
      );

      return hasMatchingSubCategory;
    });
  }, [mainCategories, searchQuery]);

  // Handle using an example template
  const useExampleTemplate = (example: string) => {
    setDescription(example);
    setExamplesOpen(false);
  };

  // Clear form data from localStorage
  const clearFormData = () => {
    localStorage.removeItem(FORM_STORAGE_KEY);
  };

  // Generate a title from the description
  const generateTitle = (description: string) => {
    if (!description || description.trim() === '') {
      return 'New Want';
    }
    
    // Take first 50 characters of description and add ellipsis if longer
    const words = description.trim().split(' ');
    let title = '';
    
    for (const word of words) {
      if ((title + ' ' + word).length <= 50) {
        title += (title ? ' ' : '') + word;
      } else {
        break;
      }
    }
    
    return title.length < description.trim().length ? `${title}...` : title;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If not on the review step, go to review
    if (currentStep !== 4) {
      setCurrentStep(4);
      return;
    }
    
    setIsSubmitting(true);
    setError('');

    try {
      // Auto-generate title from description
      const generatedTitle = generateTitle(description);
      
      const willData = {
        title: generatedTitle,
        description,
        budget: budgetType === 'FIXED' ? parseFloat(budget) : (budgetType === 'FREE' ? 0 : null),
        location,
        category,
        currency: budgetType === 'FIXED' ? currency : null,
        fulfillmentTimeType,
        fulfillmentTime: fulfillmentTimeType === 'SPECIFIC_DATE' ? new Date(fulfillmentDate).toISOString() : null,
        recurrencePattern
      };

      // Get only the File objects from the media array
      const mediaFiles = media
        .filter(item => item.file)
        .map(item => item.file);

      const response = await createWillWithMedia(willData, mediaFiles);
      
      // Clear stored form data on successful submission
      clearFormData();
      
      if (onSuccess) {
        onSuccess(response);
      }
    } catch (err) {
      console.error('Error creating want:', err);
      setError('Failed to create want. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
  };

  const handleCancel = () => {
    // Clear form data when user cancels
    clearFormData();
    if (onCancel) {
      onCancel();
    }
  };

  // Find the category display name from the selected category value
  const getCategoryDisplayName = (categoryValue: string): string => {
    // First check main categories
    const mainCategory = mainCategories.find(cat => cat.value === categoryValue);
    if (mainCategory) return mainCategory.displayName;
    
    // Then check subcategories
    for (const mainCat of mainCategories) {
      const subcategories = getSubcategories(mainCat.value);
      const subcat = subcategories.find(sub => sub.value === categoryValue);
      if (subcat) return `${mainCat.displayName} > ${subcat.displayName}`;
    }
    
    return categoryValue; // Fallback to the value if not found
  };

  const renderStep1 = () => (
    <>
      <h3 className="step-title">WHAT DO YOU WANT?</h3>
      
      <div className="form-group">
        <label htmlFor="description">Tell us what you want - We'll help make it happen</label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe what you want in detail"
          rows={6}
          required
        />
        <p className="form-helper">Need inspiration? <a href="#" onClick={(e) => { e.preventDefault(); setExamplesOpen(true); }}>See examples</a></p>
      </div>
      
      <div className="form-group">
        <label htmlFor="category">Category</label>
        <Select
          value={category}
          onValueChange={setCategory}
          required
          open={isCategoryOpen}
          onOpenChange={setIsCategoryOpen}
        >
          <SelectTrigger className="w-full" onClick={() => setIsCategoryOpen(!isCategoryOpen)}>
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent className="bg-white border rounded-lg shadow-lg max-h-[400px] overflow-auto">
            <div className="sticky top-0 p-2 bg-white border-b z-10">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="text"
                  placeholder="Search categories..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCategoryOpen(true);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="pl-8 bg-white border-gray-200 focus:border-gray-300 focus:ring-1 focus:ring-gray-200"
                />
              </div>
            </div>
            {filteredCategories.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No categories found matching "{searchQuery}"
              </div>
            ) : (
              filteredCategories.map((mainCategory: CategoryType, index) => {
                const subCategories = getSubcategories(mainCategory.value);
                const filteredSubCategories = subCategories.filter(subCategory => 
                  !searchQuery || 
                  subCategory.displayName.toLowerCase().includes(searchQuery.toLowerCase())
                );

                return (
                  <div key={mainCategory.value}>
                    {index > 0 && <div className="h-px bg-gray-200 my-2 mx-2"></div>}
                    <SelectGroup>
                      <SelectLabel className="font-bold text-gray-900 py-2 px-3 bg-gray-50 rounded-md my-1 mx-1.5 block">
                        {mainCategory.displayName}
                      </SelectLabel>
                      <SelectItem 
                        key={mainCategory.value} 
                        value={mainCategory.value}
                        className="cursor-pointer hover:bg-[#222222] hover:text-white data-[state=checked]:text-white data-[state=checked]:bg-[#222222] pl-4 border-l-2 border-transparent hover:border-[#222222] ml-3 text-gray-700"
                      >
                        {mainCategory.displayName}
                      </SelectItem>
                      {filteredSubCategories.map((subCategory: CategoryType) => (
                        <SelectItem 
                          key={subCategory.value} 
                          value={subCategory.value}
                          className="cursor-pointer hover:bg-[#222222] hover:text-white data-[state=checked]:text-white data-[state=checked]:bg-[#222222] pl-8 ml-4 text-gray-600 text-sm border-l-2 border-transparent hover:border-[#222222]"
                        >
                          {subCategory.displayName}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </div>
                );
              })
            )}
          </SelectContent>
        </Select>
      </div>
      
      <div className="form-actions">
        <button
          type="button"
          onClick={handleCancel}
          className="cancel-button"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={nextStep}
          className="submit-button"
          disabled={!description || !category}
        >
          Next
        </button>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <h3 className="step-title">STEP 2: When & Where?</h3>
      
      <div className="form-group">
        <label>When do you want it?</label>
        <div className="radio-group">
          <div className="radio-option">
            <input
              type="radio"
              id="asap"
              name="fulfillmentTimeType"
              value="ASAP"
              checked={fulfillmentTimeType === 'ASAP'}
              onChange={() => setFulfillmentTimeType('ASAP')}
            />
            <label htmlFor="asap">Now (ASAP)</label>
          </div>
          <div className="radio-option">
            <input
              type="radio"
              id="this_week"
              name="fulfillmentTimeType"
              value="THIS_WEEK"
              checked={fulfillmentTimeType === 'THIS_WEEK'}
              onChange={() => setFulfillmentTimeType('THIS_WEEK')}
            />
            <label htmlFor="this_week">This week</label>
          </div>
          <div className="radio-option">
            <input
              type="radio"
              id="specific_date"
              name="fulfillmentTimeType"
              value="SPECIFIC_DATE"
              checked={fulfillmentTimeType === 'SPECIFIC_DATE'}
              onChange={() => setFulfillmentTimeType('SPECIFIC_DATE')}
            />
            <label htmlFor="specific_date">Pick a date</label>
          </div>
        </div>
        
        {fulfillmentTimeType === 'SPECIFIC_DATE' && (
          <div className="form-group">
            <input
              type="date"
              value={fulfillmentDate}
              onChange={(e) => setFulfillmentDate(e.target.value)}
              className="date-picker"
              required
            />
          </div>
        )}
      </div>
      
      <div className="form-group">
        <div className="checkbox-group">
          <input
            type="checkbox"
            id="is_recurring"
            checked={recurrencePattern !== 'NONE'}
            onChange={(e) => setRecurrencePattern(e.target.checked ? 'DAILY' : 'NONE')}
          />
          <label htmlFor="is_recurring">Recurring</label>
        </div>
        
        {recurrencePattern !== 'NONE' && (
          <div className="select-group">
            <select
              value={recurrencePattern}
              onChange={(e) => setRecurrencePattern(e.target.value)}
            >
              <option value="DAILY">Daily</option>
              <option value="WEEKLY">Weekly</option>
              <option value="MONTHLY">Monthly</option>
              <option value="YEARLY">Yearly</option>
              <option value="CUSTOM">Custom</option>
            </select>
          </div>
        )}
      </div>
      
      <div className="form-group">
        <label htmlFor="location">Where?</label>
        <input
          id="location"
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="City, area, or online"
        />
      </div>
      
      <div className="form-actions">
        <button
          type="button"
          onClick={prevStep}
          className="cancel-button"
        >
          Back
        </button>
        <button
          type="button"
          onClick={nextStep}
          className="submit-button"
        >
          Next
        </button>
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <h3 className="step-title">STEP 3: How Much?</h3>
      
      <div className="form-group">
        <label>How much are you willing to pay?</label>
        <div className="radio-group">
          <div className="radio-option">
            <input
              type="radio"
              id="fixed"
              name="budgetType"
              value="FIXED"
              checked={budgetType === 'FIXED'}
              onChange={() => setBudgetType('FIXED')}
            />
            <label htmlFor="fixed">Fixed amount</label>
          </div>
          <div className="radio-option">
            <input
              type="radio"
              id="open"
              name="budgetType"
              value="OPEN"
              checked={budgetType === 'OPEN'}
              onChange={() => setBudgetType('OPEN')}
            />
            <label htmlFor="open">Open to offers</label>
          </div>
          <div className="radio-option">
            <input
              type="radio"
              id="free"
              name="budgetType"
              value="FREE"
              checked={budgetType === 'FREE'}
              onChange={() => setBudgetType('FREE')}
            />
            <label htmlFor="free">FREE</label>
          </div>
        </div>
        
        {budgetType === 'FIXED' && (
          <div className="form-row">
            <div className="form-group">
              <input
                id="budget"
                type="number"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                required
                placeholder="Enter your budget"
                min="0"
                step="0.01"
              />
            </div>
            
            <div className="form-group">
              <select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                <option value="ILS">ILS</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>
        )}
      </div>
      
      <MediaUploadForWill
        existingMedia={[]}
        onMediaChange={setMedia}
        maxFiles={5}
      />
      
      <div className="form-actions">
        <button
          type="button"
          onClick={prevStep}
          className="cancel-button"
          disabled={isSubmitting}
        >
          Back
        </button>
        <button
          type="button"
          onClick={nextStep}
          className="submit-button"
          disabled={isSubmitting || (budgetType === 'FIXED' && !budget)}
        >
          Review
        </button>
      </div>
    </>
  );

  // New step 4 for Review
  const renderStep4 = () => {
    // Format the time/date for display
    const formatTime = () => {
      if (fulfillmentTimeType === 'ASAP') return 'As soon as possible';
      if (fulfillmentTimeType === 'THIS_WEEK') return 'This week';
      if (fulfillmentTimeType === 'SPECIFIC_DATE' && fulfillmentDate) {
        return new Date(fulfillmentDate).toLocaleDateString();
      }
      return '';
    };

    // Format the budget for display
    const formatBudget = () => {
      if (budgetType === 'OPEN') return 'Open to offers';
      if (budgetType === 'FREE') return 'FREE';
      if (budgetType === 'FIXED' && budget) {
        return `${budget} ${currency}`;
      }
      return '';
    };

    // Toggle edit mode for a section
    const toggleEditSection = (section: string) => {
      setEditingSection(editingSection === section ? null : section);
    };

    // Add a validation function to check if date is required but missing
    const isDateRequired = () => {
      return fulfillmentTimeType === 'SPECIFIC_DATE' && !fulfillmentDate;
    };

    return (
      <>
        <h3 className="step-title">REVIEW YOUR WANT</h3>
        
        <div className="review-container">
          <div className="review-card">
            <div className="review-header">
              <h4 className="review-title">{previewTitle}</h4>
              {category && (
                <div className="review-category">
                  {getCategoryDisplayName(category)}
                </div>
              )}
            </div>
            
            {/* Description Section */}
            <div className="review-section">
              <div className="review-section-header">
                <h5>Description</h5>
                <button 
                  type="button" 
                  className="edit-button"
                  onClick={() => toggleEditSection('description')}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  {editingSection === 'description' ? 'Done' : 'Edit'}
                </button>
              </div>
              
              {editingSection === 'description' ? (
                <div className="edit-form-field">
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="edit-textarea"
                    rows={6}
                    placeholder="Describe what you want in detail"
                    required
                  />
                </div>
              ) : (
                <div className="review-content">{description}</div>
              )}
            </div>
            
            {/* Category Section */}
            <div className="review-section">
              <div className="review-section-header">
                <h5>Category</h5>
                <button 
                  type="button" 
                  className="edit-button"
                  onClick={() => toggleEditSection('category')}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  {editingSection === 'category' ? 'Done' : 'Edit'}
                </button>
              </div>
              
              {editingSection === 'category' ? (
                <div className="edit-form-field">
                  <Select
                    value={category}
                    onValueChange={(value) => {
                      setCategory(value);
                      // Optionally close edit mode after selection
                      // toggleEditSection('category');
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border rounded-lg shadow-lg max-h-[300px] overflow-auto">
                      {mainCategories.map((mainCategory: CategoryType, index) => {
                        const subCategories = getSubcategories(mainCategory.value);
                        
                        return (
                          <div key={mainCategory.value}>
                            {index > 0 && <div className="h-px bg-gray-200 my-2 mx-2"></div>}
                            <SelectGroup>
                              <SelectLabel className="font-bold text-gray-900 py-2 px-3 bg-gray-50 rounded-md my-1 mx-1.5 block">
                                {mainCategory.displayName}
                              </SelectLabel>
                              <SelectItem 
                                key={mainCategory.value} 
                                value={mainCategory.value}
                                className="cursor-pointer hover:bg-[#222222] hover:text-white data-[state=checked]:text-white data-[state=checked]:bg-[#222222] pl-4 border-l-2 border-transparent hover:border-[#222222] ml-3 text-gray-700"
                              >
                                {mainCategory.displayName}
                              </SelectItem>
                              {subCategories.map((subCategory: CategoryType) => (
                                <SelectItem 
                                  key={subCategory.value} 
                                  value={subCategory.value}
                                  className="cursor-pointer hover:bg-[#222222] hover:text-white data-[state=checked]:text-white data-[state=checked]:bg-[#222222] pl-8 ml-4 text-gray-600 text-sm border-l-2 border-transparent hover:border-[#222222]"
                                >
                                  {subCategory.displayName}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </div>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="review-content">{getCategoryDisplayName(category)}</div>
              )}
            </div>
            
            {/* Time & Location Section */}
            <div className="review-section">
              <div className="review-section-header">
                <h5>Time & Location</h5>
                <button 
                  type="button" 
                  className="edit-button"
                  onClick={() => toggleEditSection('time-location')}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  {editingSection === 'time-location' ? 'Done' : 'Edit'}
                </button>
              </div>
              
              {editingSection === 'time-location' ? (
                <div className="edit-form-field">
                  <div className="edit-field-group">
                    <label className="edit-label">When do you want it?</label>
                    <div className="radio-group">
                      <div className="radio-option">
                        <input
                          type="radio"
                          id="review-asap"
                          name="review-fulfillmentTimeType"
                          value="ASAP"
                          checked={fulfillmentTimeType === 'ASAP'}
                          onChange={() => setFulfillmentTimeType('ASAP')}
                        />
                        <label htmlFor="review-asap">Now (ASAP)</label>
                      </div>
                      <div className="radio-option">
                        <input
                          type="radio"
                          id="review-this_week"
                          name="review-fulfillmentTimeType"
                          value="THIS_WEEK"
                          checked={fulfillmentTimeType === 'THIS_WEEK'}
                          onChange={() => setFulfillmentTimeType('THIS_WEEK')}
                        />
                        <label htmlFor="review-this_week">This week</label>
                      </div>
                      <div className="radio-option">
                        <input
                          type="radio"
                          id="review-specific_date"
                          name="review-fulfillmentTimeType"
                          value="SPECIFIC_DATE"
                          checked={fulfillmentTimeType === 'SPECIFIC_DATE'}
                          onChange={() => setFulfillmentTimeType('SPECIFIC_DATE')}
                        />
                        <label htmlFor="review-specific_date">Pick a date</label>
                      </div>
                    </div>
                    
                    {fulfillmentTimeType === 'SPECIFIC_DATE' && (
                      <div className="mt-2">
                        <input
                          type="date"
                          value={fulfillmentDate}
                          onChange={(e) => setFulfillmentDate(e.target.value)}
                          className={`date-picker w-full ${isDateRequired() ? 'border-red-500' : ''}`}
                          required
                        />
                        {isDateRequired() && (
                          <p className="text-red-500 text-xs mt-1">Please select a date</p>
                        )}
                      </div>
                    )}
                    
                    <div className="mt-4">
                      <div className="checkbox-group">
                        <input
                          type="checkbox"
                          id="review-is_recurring"
                          checked={recurrencePattern !== 'NONE'}
                          onChange={(e) => setRecurrencePattern(e.target.checked ? 'DAILY' : 'NONE')}
                        />
                        <label htmlFor="review-is_recurring">Recurring</label>
                      </div>
                      
                      {recurrencePattern !== 'NONE' && (
                        <div className="select-group mt-2">
                          <select
                            value={recurrencePattern}
                            onChange={(e) => setRecurrencePattern(e.target.value)}
                            className="w-full"
                          >
                            <option value="DAILY">Daily</option>
                            <option value="WEEKLY">Weekly</option>
                            <option value="MONTHLY">Monthly</option>
                            <option value="YEARLY">Yearly</option>
                            <option value="CUSTOM">Custom</option>
                          </select>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4">
                      <label className="edit-label" htmlFor="review-location">Where?</label>
                      <input
                        id="review-location"
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="City, area, or online"
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="review-details">
                  {formatTime() && (
                    <div className="review-detail">
                      <span className="detail-label">When:</span>
                      <span className="detail-value">{formatTime()}</span>
                    </div>
                  )}
                  
                  {isDateRequired() && (
                    <div className="review-detail error-detail">
                      <span className="detail-label">Error:</span>
                      <span className="detail-value text-red-500">Please select a date</span>
                    </div>
                  )}
                  
                  {recurrencePattern !== 'NONE' && (
                    <div className="review-detail">
                      <span className="detail-label">Recurrence:</span>
                      <span className="detail-value">
                        {recurrencePattern.charAt(0) + recurrencePattern.slice(1).toLowerCase()}
                      </span>
                    </div>
                  )}
                  
                  {location && (
                    <div className="review-detail">
                      <span className="detail-label">Where:</span>
                      <span className="detail-value">{location}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Budget Section */}
            <div className="review-section">
              <div className="review-section-header">
                <h5>Budget</h5>
                <button 
                  type="button" 
                  className="edit-button"
                  onClick={() => toggleEditSection('budget')}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  {editingSection === 'budget' ? 'Done' : 'Edit'}
                </button>
              </div>
              
              {editingSection === 'budget' ? (
                <div className="edit-form-field">
                  <div className="edit-field-group">
                    <label className="edit-label">How much are you willing to pay?</label>
                    <div className="radio-group">
                      <div className="radio-option">
                        <input
                          type="radio"
                          id="review-fixed"
                          name="review-budgetType"
                          value="FIXED"
                          checked={budgetType === 'FIXED'}
                          onChange={() => setBudgetType('FIXED')}
                        />
                        <label htmlFor="review-fixed">Fixed amount</label>
                      </div>
                      <div className="radio-option">
                        <input
                          type="radio"
                          id="review-open"
                          name="review-budgetType"
                          value="OPEN"
                          checked={budgetType === 'OPEN'}
                          onChange={() => setBudgetType('OPEN')}
                        />
                        <label htmlFor="review-open">Open to offers</label>
                      </div>
                      <div className="radio-option">
                        <input
                          type="radio"
                          id="review-free"
                          name="review-budgetType"
                          value="FREE"
                          checked={budgetType === 'FREE'}
                          onChange={() => setBudgetType('FREE')}
                        />
                        <label htmlFor="review-free">FREE</label>
                      </div>
                    </div>
                    
                    {budgetType === 'FIXED' && (
                      <div className="form-row mt-3">
                        <div className="form-group">
                          <input
                            id="review-budget"
                            type="number"
                            value={budget}
                            onChange={(e) => setBudget(e.target.value)}
                            required
                            placeholder="Enter your budget"
                            min="0"
                            step="0.01"
                            className="w-full"
                          />
                        </div>
                        
                        <div className="form-group mt-2">
                          <select
                            id="review-currency"
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            className="w-full"
                          >
                            <option value="ILS">ILS</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="review-budget">{formatBudget()}</div>
              )}
            </div>
            
            {/* Media Section */}
            <div className="review-section">
              <div className="review-section-header">
                <h5>Media</h5>
                <button 
                  type="button" 
                  className="edit-button"
                  onClick={() => toggleEditSection('media')}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  {editingSection === 'media' ? 'Done' : 'Edit'}
                </button>
              </div>
              
              {editingSection === 'media' ? (
                <div className="edit-form-field">
                  <MediaUploadForWill
                    existingMedia={media}
                    onMediaChange={setMedia}
                    maxFiles={5}
                  />
                </div>
              ) : (
                <div className="review-media">
                  {media.length > 0 ? (
                    media.map((item, index) => (
                      <div key={index} className="media-preview">
                        {item.previewUrl && (
                          <img 
                            src={item.previewUrl} 
                            alt={`Media ${index + 1}`} 
                            className="media-thumbnail"
                          />
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 text-sm italic">No media attached</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="form-actions">
          <button
            type="button"
            onClick={prevStep}
            className="cancel-button"
            disabled={isSubmitting}
          >
            Back
          </button>
          <button
            type="submit"
            className="submit-button"
            disabled={
              isSubmitting || 
              !description || 
              !category || 
              (budgetType === 'FIXED' && !budget) || 
              editingSection !== null ||
              isDateRequired()
            }
          >
            {isSubmitting ? 'Creating...' : 'Create Want'}
          </button>
        </div>
      </>
    );
  };
  
  return (
    <form onSubmit={handleSubmit} className="will-form">
      {error && <div className="error-message">{error}</div>}
      
      <div className="step-indicators">
        <div 
          className={`step-indicator ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}
          onClick={() => currentStep > 1 ? goToStep(1) : null}
        >
          {currentStep > 1 ? <Check className="h-4 w-4" /> : '1'}
        </div>
        <div className="step-connector"></div>
        <div 
          className={`step-indicator ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}
          onClick={() => currentStep > 2 ? goToStep(2) : null}
        >
          {currentStep > 2 ? <Check className="h-4 w-4" /> : '2'}
        </div>
        <div className="step-connector"></div>
        <div 
          className={`step-indicator ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}
          onClick={() => currentStep > 3 ? goToStep(3) : null}
        >
          {currentStep > 3 ? <Check className="h-4 w-4" /> : '3'} 
        </div>
        <div className="step-connector"></div>
        <div className={`step-indicator ${currentStep >= 4 ? 'active' : ''}`}>
          4
        </div>
      </div>
      
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
      {currentStep === 4 && renderStep4()}

      {/* Examples Modal */}
      <Dialog open={examplesOpen} onOpenChange={setExamplesOpen}>
        <DialogContent className="max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold mb-2">Want Examples & Templates</DialogTitle>
            <DialogDescription>
              Click on any example to use it as your want description. You can edit it afterward.
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 space-y-6">
            {EXAMPLE_TEMPLATES.map((templateGroup, index) => (
              <div key={index} className="space-y-3">
                <h4 className="font-semibold text-lg">{templateGroup.category}</h4>
                <div className="space-y-3">
                  {templateGroup.examples.map((example, exIndex) => (
                    <div
                      key={exIndex}
                      className="p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => useExampleTemplate(example)}
                    >
                      <p>{example}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </form>
  );
};

export default WillForm;
